import { useEffect, useState, useCallback, useMemo, lazy, Suspense } from 'react';
import { api } from '../api.js';
import { StoreCard, ClubCard, EventCard, FragCard, OnlineCard } from './near/cards.jsx';
import {
  AddStoreForm,
  AddOnlineForm,
  AddEventForm,
  AddFragForm,
  SuggestEditForm,
  LocationBar,
} from './near/forms.jsx';
import {
  FILTERS,
  radiusChoices,
  openState,
  priceLabel,
  readSavedNearLoc,
  writeSavedNearLoc,
  cityFromLabel,
  nearFeedUrl,
  distanceLabel,
} from './near/helpers.js';
import { placeMatchesText } from '../../../shared/near-region.js';
import { distanceUnitForLocale } from '../../../shared/distance-unit.js';
import {
  page,
  pageInner,
  header,
  eyebrow,
  closeBtn,
  fieldInput,
  sectionLabel,
  chip,
  miniChip,
  btnGhost,
  btnPrimary,
} from './near/styles.js';

const NearMap = lazy(() => import('./NearMap.jsx'));

// Near You — local reef discovery. Page shell only: location resolution
// (GPS or typed ZIP/city), the GET /api/near fetch, filtering, and
// layout. Cards live in near/cards.jsx, forms in near/forms.jsx, pure
// logic in near/helpers.js, and the capability taxonomy comes from the
// near_taxonomy config (near/taxonomy.js). Location stays coarse
// (~1 km) and is never sent anywhere it could be stored.

// Filled, not outlined: the outline version sat at the bottom of the page
// and blended in, so keepers could not find where to add an event.
const addBtn = {
  background: 'color-mix(in srgb, var(--text) 12%, transparent)',
  border: '1px solid color-mix(in srgb, var(--text) 20%, transparent)',
  fontWeight: 600,
  letterSpacing: '0.06em',
  padding: '10px 6px',
  whiteSpace: 'nowrap',
};

export function NearView({ user, onClose }) {
  const [coords, setCoords] = useState(null); // {lat,lng} | 'denied' | 'unsupported' | null
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all');
  const [radius, setRadius] = useState(50);
  const [query, setQuery] = useState('');
  const [openNow, setOpenNow] = useState(false);
  const [mapMode, setMapMode] = useState(false);
  const [adding, setAdding] = useState(null); // null | 'store' | 'club' | 'online' | 'event' | 'frag'
  // Online stores need no location; loaded once.
  const [online, setOnline] = useState([]);
  useEffect(() => {
    let alive = true;
    api('/api/near/online')
      .then((r) => alive && setOnline(r.stores || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  const [editing, setEditing] = useState(null); // a place object being suggested-edit'd
  const [locLabel, setLocLabel] = useState('');
  // A typed state: the whole state is listed, no mile limit.
  const [area, setArea] = useState(null);
  const gpsAvailable = typeof navigator !== 'undefined' && 'geolocation' in navigator;

  const requestLocation = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setCoords('unsupported');
      return;
    }
    setCoords(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // Coarse to ~1 km before it leaves the device — privacy first.
        setCoords({
          lat: Math.round(pos.coords.latitude * 100) / 100,
          lng: Math.round(pos.coords.longitude * 100) / 100,
        });
        setLocLabel('your area');
        setArea(null);
        // The keeper explicitly switched to GPS — drop any remembered
        // manual location so it doesn't silently win again next visit.
        writeSavedNearLoc(null);
      },
      () => setCoords('denied'),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
    );
  }, []);

  // A typed ZIP/city/state (geocoded server-side) sets the origin without
  // any GPS — the desktop / no-share path. Remembered on-device so it's
  // sticky between visits.
  const setManualLocation = useCallback((c, label, typedArea = null) => {
    setCoords(c);
    setLocLabel(label || '');
    setArea(typedArea || null);
    writeSavedNearLoc({ lat: c.lat, lng: c.lng, label: label || '', area: typedArea || null });
  }, []);

  useEffect(() => {
    // Prefer a remembered manual location; otherwise ask the device.
    const saved = readSavedNearLoc();
    if (saved) {
      setCoords({ lat: saved.lat, lng: saved.lng });
      setLocLabel(saved.label || '');
      setArea(saved.area || null);
      return;
    }
    requestLocation();
  }, [requestLocation]);

  useEffect(() => {
    if (!coords || typeof coords === 'string') return;
    let alive = true;
    setLoading(true);
    setErr('');
    api(nearFeedUrl(coords, { radius, area }))
      .then((r) => alive && setData(r))
      .catch((e) => alive && setErr(e.message || 'Could not load nearby places'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [coords, radius, area]);

  // Miles or km by the country the keeper is in (the server reads it),
  // else by the phone's language setting.
  const unit =
    data?.distance_unit ||
    distanceUnitForLocale(typeof navigator !== 'undefined' ? navigator.language : '');
  const q = query.trim().toLowerCase();
  const matchesQuery = (p) => placeMatchesText(p, q);
  // "All of X" only once the server has listed the state; until then (or
  // if it refused the box) the radius controls stay.
  const shownArea = area && data?.area ? area : null;
  const allPlaces = (data?.places || []).filter(matchesQuery);
  // Open-now applies only to things with a real schedule; a place with no
  // hours on file isn't excluded (we don't know it's closed).
  const passOpen = (p) => {
    if (!openNow) return true;
    const os = openState(p.hours, { tz: p.tz });
    return !os || os.text || os.open;
  };
  const stores = allPlaces.filter((p) => p.kind !== 'club').filter(passOpen);
  const clubs = allPlaces.filter((p) => p.kind === 'club').filter(passOpen);
  const events = (data?.events || []).filter(
    (e) =>
      !q ||
      (e.title || '').toLowerCase().includes(q) ||
      (e.venue_text || '').toLowerCase().includes(q)
  );
  const frags = (data?.frags || []).filter(
    (fr) =>
      !q ||
      (fr.title || '').toLowerCase().includes(q) ||
      (fr.species || '').toLowerCase().includes(q) ||
      (fr.city || '').toLowerCase().includes(q)
  );
  const showStores = filter === 'all' || filter === 'store';
  const showClubs = filter === 'all' || filter === 'club';
  const showEvents = filter === 'all' || filter === 'event';
  const showFrags = filter === 'all' || filter === 'frag';
  const showOnline = filter === 'all' || filter === 'online';
  const onlineStores = online.filter(
    (p) =>
      !q || (p.name || '').toLowerCase().includes(q) || (p.website || '').toLowerCase().includes(q)
  );
  const nothing =
    (!showStores || !stores.length) &&
    (!showClubs || !clubs.length) &&
    (!showEvents || !events.length) &&
    (!showFrags || !frags.length) &&
    (!showOnline || !onlineStores.length);
  const dismissFrag = (id) =>
    setData((d) => (d ? { ...d, frags: (d.frags || []).filter((x) => x.id !== id) } : d));

  // Pins for the map — respect the active filter so Map and List show the
  // same set. Events/frags carry their own coords; some may be null.
  // Memoized on the raw data + filters (NOT the derived arrays, whose
  // identity changes every render) so NearMap's effect input is stable.
  const mapPoints = useMemo(() => {
    return [
      ...(showStores
        ? stores.map((p) => ({ ...p, kind: p.kind || 'store', name: p.name, sub: p.city }))
        : []),
      ...(showClubs ? clubs.map((p) => ({ ...p, kind: 'club', name: p.name, sub: p.city })) : []),
      ...(showEvents
        ? events.map((e) => ({ ...e, kind: 'event', name: e.title, sub: e.venue_text }))
        : []),
      ...(showFrags
        ? frags.map((fr) => ({ ...fr, kind: 'frag', name: fr.title, sub: priceLabel(fr) }))
        : []),
    ].filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  }, [data, filter, q, openNow]);

  return (
    <div style={page}>
      <div style={pageInner}>
        <div style={header}>
          <div>
            <div className="font-heading" style={eyebrow}>
              Around the reef
            </div>
            <h2 style={{ margin: '2px 0 0', fontSize: 24, letterSpacing: '-0.01em' }}>Near you</h2>
          </div>
          {onClose && (
            <button type="button" onClick={onClose} style={closeBtn} aria-label="Back">
              &#10005;
            </button>
          )}
        </div>

        {editing ? (
          <SuggestEditForm
            place={editing}
            onDone={() => setEditing(null)}
            onCancel={() => setEditing(null)}
          />
        ) : adding === 'store' || adding === 'club' ? (
          <AddStoreForm
            kind={adding}
            coords={coords}
            onDone={() => setAdding(null)}
            onCancel={() => setAdding(null)}
          />
        ) : adding === 'online' ? (
          <AddOnlineForm onDone={() => setAdding(null)} onCancel={() => setAdding(null)} />
        ) : adding === 'event' ? (
          <AddEventForm
            coords={coords}
            area={area}
            onDone={() => setAdding(null)}
            onCancel={() => setAdding(null)}
          />
        ) : adding === 'frag' ? (
          <AddFragForm
            coords={coords}
            defaultCity={area ? '' : cityFromLabel(locLabel)}
            onDone={() => setAdding(null)}
            onCancel={() => setAdding(null)}
          />
        ) : (
          <>
            <LocationBar
              label={locLabel}
              wholeArea={!!shownArea}
              onSet={setManualLocation}
              onGps={requestLocation}
              gpsAvailable={gpsAvailable}
            />
            {!coords || typeof coords === 'string' ? (
              <>
                <div style={{ padding: '22px 0', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  {coords === null
                    ? 'Finding your location — or enter a ZIP, city, or state above.'
                    : 'We couldn’t use your device location. Enter a ZIP, city, or state above to see what’s near you.'}
                </div>
                {/* Online stores need no location. */}
                {online.length ? (
                  <div
                    style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 16 }}
                  >
                    <div className="font-heading" style={sectionLabel}>
                      Online stores
                    </div>
                    {online.map((p) => (
                      <OnlineCard key={p.id} p={p} />
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <input
                  style={{ ...fieldInput, marginTop: 14, minHeight: 44 }}
                  placeholder="Search stores, clubs, events…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <div style={{ display: 'flex', gap: 7, padding: '10px 0 4px', flexWrap: 'wrap' }}>
                  {FILTERS.map((f) => (
                    <button
                      key={f.k}
                      type="button"
                      onClick={() => setFilter(f.k)}
                      className="font-heading"
                      style={chip(filter === f.k)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 7,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    padding: '2px 0 4px',
                  }}
                >
                  {shownArea ? (
                    <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>
                      All of {shownArea.name}
                    </span>
                  ) : (
                    <>
                      <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>Within</span>
                      {radiusChoices(unit).map((r) => (
                        <button
                          key={r.label}
                          type="button"
                          onClick={() => setRadius(r.mi)}
                          className="font-heading"
                          style={miniChip(Math.abs(radius - r.mi) < 0.5)}
                        >
                          {r.label}
                        </button>
                      ))}
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => setOpenNow((v) => !v)}
                    className="font-heading"
                    style={miniChip(openNow)}
                  >
                    {openNow ? '✓ Open now' : 'Open now'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMapMode((v) => !v)}
                    className="font-heading"
                    style={miniChip(mapMode)}
                  >
                    {mapMode ? 'List' : 'Map'}
                  </button>
                </div>

                {loading ? (
                  <div style={{ padding: '24px 0', color: 'var(--text-muted)' }}>
                    Finding what’s near you…
                  </div>
                ) : err ? (
                  <div style={{ padding: '16px 0', color: 'var(--alert, #d64545)' }}>{err}</div>
                ) : mapMode ? (
                  <div style={{ marginTop: 12 }}>
                    <Suspense
                      fallback={
                        <div style={{ padding: '24px 0', color: 'var(--text-muted)' }}>
                          Loading map…
                        </div>
                      }
                    >
                      <NearMap
                        points={mapPoints}
                        center={typeof coords === 'object' ? coords : null}
                      />
                    </Suspense>
                    <div style={{ fontSize: 13, color: 'var(--text-dim)', marginTop: 8 }}>
                      {mapPoints.length} on the map · tap a pin for details
                    </div>
                  </div>
                ) : nothing ? (
                  <div style={{ padding: '24px 0', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                    {query || openNow
                      ? shownArea
                        ? 'Nothing matches those filters right now. Try clearing the search.'
                        : 'Nothing matches those filters right now. Try widening the radius or clearing the search.'
                      : shownArea
                        ? `Nothing listed in ${shownArea.name} yet. Add a store, club or event you know with the buttons below.`
                        : 'Nothing near you yet. TankSensei is just getting started in your area — check back soon, or add a store, club, or event you know with the buttons below.'}
                  </div>
                ) : (
                  <div
                    style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 16 }}
                  >
                    {showStores && stores.length ? (
                      <>
                        <div className="font-heading" style={sectionLabel}>
                          Stores &amp; sources
                        </div>
                        {stores.map((p) => (
                          <StoreCard key={p.id} p={p} onSuggestEdit={setEditing} unit={unit} />
                        ))}
                      </>
                    ) : null}
                    {showClubs && clubs.length ? (
                      <>
                        <div className="font-heading" style={sectionLabel}>
                          Reef clubs
                        </div>
                        {clubs.map((p) => (
                          <ClubCard key={p.id} p={p} onSuggestEdit={setEditing} unit={unit} />
                        ))}
                      </>
                    ) : null}
                    {showOnline && onlineStores.length ? (
                      <>
                        <div className="font-heading" style={sectionLabel}>
                          Online stores
                        </div>
                        {onlineStores.map((p) => (
                          <OnlineCard key={p.id} p={p} />
                        ))}
                      </>
                    ) : null}
                    {showEvents && events.length ? (
                      <>
                        <div className="font-heading" style={sectionLabel}>
                          Happening soon
                        </div>
                        {events.map((e) => (
                          <EventCard key={e.id} e={e} />
                        ))}
                      </>
                    ) : null}
                    {showFrags && frags.length ? (
                      <>
                        <div className="font-heading" style={sectionLabel}>
                          Frags &amp; corals for sale
                        </div>
                        {frags.map((fr) => (
                          <FragCard
                            key={fr.id}
                            p={fr}
                            isOwner={!!user && fr.mine === true}
                            onSold={dismissFrag}
                            unit={unit}
                          />
                        ))}
                      </>
                    ) : null}
                  </div>
                )}
              </>
            )}

            {/* Below the results: on a phone the stores come first, and the
                tip and add buttons wait under them (audit 2026-10-03). */}
            <QuickTip coords={coords} area={locLabel} unit={unit} />
            <div
              style={{
                fontSize: 13,
                color: 'var(--text-dim)',
                marginTop: 12,
                letterSpacing: '0.04em',
              }}
            >
              Or add it yourself:
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              {[
                ['store', '+ Store'],
                ['club', '+ Club'],
                ['online', '+ Online store'],
                ['event', '+ Event'],
                ['frag', '+ Frag'],
              ].map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setAdding(k)}
                  className="font-heading"
                  style={{ ...btnGhost, ...addBtn, flex: 1 }}
                >
                  {label}
                </button>
              ))}
            </div>

            <div style={{ fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.5, marginTop: 10 }}>
              Your location is rounded to about 1 km before it leaves your phone. A tip you send
              keeps that rough area so we can find the place.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// "Just tell us": one line, no form (owner, 2026-09-26: filling in the
// full form is the reason nobody adds anything). Since 2026-10-21 the map is
// asked first ("Is this it?"): picking one of its matches sends a full
// listing for review, filled in from the store's website; "None of these"
// sends the tip as before. The area sent is the same coarse origin Near
// You uses.
function QuickTip({ coords, area, unit = 'mi' }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState('');
  const [err, setErr] = useState('');
  const [matches, setMatches] = useState(null);
  const [kind, setKind] = useState('store');
  const loc = coords && typeof coords !== 'string' ? { lat: coords.lat, lng: coords.lng } : null;
  const sendTip = async ({ declined = false } = {}) => {
    await api('/api/near/tips', {
      method: 'POST',
      body: { text: text.trim(), area, ...(loc || {}), ...(declined ? { declined: true } : {}) },
    });
    setText('');
    setMatches(null);
    setSent('Thanks! We will look it up and add it. Tell us another any time.');
  };
  const send = async (e) => {
    e.preventDefault();
    if (text.trim().length < 3) return;
    setBusy(true);
    setErr('');
    try {
      let found = [];
      if (loc) {
        const q = encodeURIComponent(text.trim());
        found = (
          await api(`/api/near/match?q=${q}&lat=${loc.lat}&lng=${loc.lng}`).catch(() => null)
        )?.matches;
      }
      if (Array.isArray(found) && found.length) setMatches(found);
      else await sendTip();
    } catch (e2) {
      setErr(e2.message || 'Could not send. Try again.');
    } finally {
      setBusy(false);
    }
  };
  const pick = async (m) => {
    setBusy(true);
    setErr('');
    try {
      await api('/api/near/places', {
        method: 'POST',
        body: {
          name: m.name,
          kind,
          address: m.address || '',
          website: m.website || '',
          phone: m.phone || '',
          hours: m.hours || null,
          lat: m.lat,
          lng: m.lng,
          city: m.city || '',
          region: m.region || '',
          picked: true,
          from_map: true,
        },
      });
      setText('');
      setMatches(null);
      setSent(
        `Thanks! ${m.name} was sent for review. We fill in the rest from their website, and it shows here once approved.`
      );
    } catch (e2) {
      setErr(e2.message || 'Could not send. Try again.');
    } finally {
      setBusy(false);
    }
  };
  const none = async () => {
    setBusy(true);
    setErr('');
    try {
      await sendTip({ declined: true });
    } catch (e2) {
      setErr(e2.message || 'Could not send. Try again.');
    } finally {
      setBusy(false);
    }
  };
  if (matches) {
    return (
      <div
        style={{
          marginTop: 12,
          padding: 14,
          borderRadius: 14,
          background: 'var(--surface)',
          border: '1px solid var(--accent-border)',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>
          Is it one of these?
        </div>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 8, flexWrap: 'wrap' }}
        >
          <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>It is a</span>
          {[
            ['store', 'Store'],
            ['club', 'Club'],
          ].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              aria-pressed={kind === k}
              className="font-heading"
              style={{ ...miniChip(kind === k), minHeight: 44 }}
            >
              {label}
            </button>
          ))}
        </div>
        {matches.map((m) => (
          <div
            key={`${m.lat},${m.lng},${m.name}`}
            style={{
              marginTop: 10,
              padding: 12,
              borderRadius: 12,
              border: '1px solid var(--surface-border)',
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{m.name}</div>
            <div
              style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.5 }}
            >
              {[m.address, distanceLabel(m.distance_mi, unit)].filter(Boolean).join(' · ')}
            </div>
            {m.listed ? (
              <div style={{ fontSize: 14, color: 'var(--cyan)', marginTop: 6 }}>
                {m.listed === 'listed'
                  ? 'Already in Near You. Thank you!'
                  : m.listed === 'removed'
                    ? 'This one asked not to be listed on TankSensei.'
                    : 'Already sent by a keeper, waiting for review.'}
              </div>
            ) : (
              <button
                type="button"
                disabled={busy}
                onClick={() => pick(m)}
                className="font-heading"
                style={{ ...btnPrimary, width: '100%', marginTop: 8, opacity: busy ? 0.6 : 1 }}
              >
                Yes, this one
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          disabled={busy}
          onClick={none}
          className="font-heading"
          style={{ ...btnGhost, width: '100%', marginTop: 10, minHeight: 44 }}
        >
          None of these, send my tip
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setMatches(null)}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            fontSize: 15,
            width: '100%',
            minHeight: 44,
            marginTop: 2,
          }}
        >
          Change what I typed
        </button>
        {err && (
          <div role="alert" style={{ fontSize: 14, color: 'var(--alert, #d64545)', marginTop: 6 }}>
            {err}
          </div>
        )}
      </div>
    );
  }
  return (
    <form
      onSubmit={send}
      style={{
        marginTop: 12,
        padding: 14,
        borderRadius: 14,
        background: 'var(--surface)',
        border: '1px solid var(--accent-border)',
      }}
    >
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>
        Know a reef store, club or event we should add?
      </div>
      {sent ? (
        <div
          role="status"
          style={{
            fontSize: 15,
            color: 'var(--text)',
            marginTop: 4,
            lineHeight: 1.5,
            fontWeight: 500,
          }}
        >
          {sent}
        </div>
      ) : (
        <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.5 }}>
          {busy
            ? 'Looking it up on the map…'
            : 'Just tell us the name, and where or when if you know. We fill in the rest.'}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <input
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (sent) setSent('');
          }}
          maxLength={500}
          placeholder="e.g. Reef Depot in Plainfield, or a frag swap on Oct 12"
          style={{ ...fieldInput, flex: 1, marginTop: 0 }}
        />
        <button
          type="submit"
          disabled={busy || text.trim().length < 3}
          className="font-heading"
          style={{
            ...btnGhost,
            background: 'var(--cyan)',
            color: 'var(--cyan-text)',
            border: 'none',
            fontWeight: 600,
            opacity: busy || text.trim().length < 3 ? 0.5 : 1,
          }}
        >
          {busy ? 'Sending' : 'Send'}
        </button>
      </div>
      {err && (
        <div style={{ fontSize: 14, color: 'var(--alert, #d64545)', marginTop: 6 }}>{err}</div>
      )}
    </form>
  );
}
